import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';

const card = { background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:20, marginBottom:16 };
const statBox = (color) => ({ background:color+'10', border:`1px solid ${color}30`, borderRadius:8, padding:'14px 18px', textAlign:'center' });

export default function Reports() {
  const [summary, setSummary] = useState(null);
  const [staff, setStaff] = useState([]);
  const [delivery, setDelivery] = useState([]);
  const [loading, setLoading] = useState(false);
  const today = new Date();
  const [dateFrom, setDateFrom] = useState(new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0,10));
  const [dateTo, setDateTo] = useState(today.toISOString().slice(0,10));

  useEffect(()=>{ load(); },[]);

  async function load() {
    setLoading(true);
    try {
      const [s,st,d] = await Promise.all([
        api.get('/reports/summary', { params:{date_from:dateFrom, date_to:dateTo} }),
        api.get('/reports/staff', { params:{date_from:dateFrom, date_to:dateTo} }),
        api.get('/reports/delivery', { params:{date_from:dateFrom, date_to:dateTo} }),
      ]);
      setSummary(s.data); setStaff(st.data||[]); setDelivery(d.data||[]);
    } catch { toast.error('Failed to load reports'); }
    finally { setLoading(false); }
  }

  const inp = { background:'#f8f9fa', border:'1px solid #dee2e6', borderRadius:6, padding:'6px 10px', fontSize:13, outline:'none' };

  return (
    <div style={{ padding:24, minHeight:'100vh', background:'#f8fafc' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h1 style={{ margin:0, fontSize:22, fontWeight:700, color:'#111827' }}>📊 Reports</h1>
          <p style={{ margin:'2px 0 0', fontSize:13, color:'#6b7280' }}>Business performance overview</p>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} style={inp} />
          <span style={{ color:'#9ca3af' }}>to</span>
          <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} style={inp} />
          <button onClick={load} disabled={loading} style={{ background:'#7c6af5', color:'#fff', border:'none', borderRadius:8, padding:'7px 18px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            {loading?'Loading...':'Search'}
          </button>
        </div>
      </div>

      {summary && (
        <>
          {/* Revenue summary */}
          <div style={card}>
            <div style={{ fontSize:14, fontWeight:700, color:'#111827', marginBottom:16 }}>💰 Revenue — {dateFrom} to {dateTo}</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
              <div style={statBox('#16a34a')}>
                <div style={{ fontSize:22, fontWeight:700, color:'#16a34a' }}>${summary.revenue.total.toFixed(2)}</div>
                <div style={{ fontSize:12, color:'#6b7280' }}>Total Revenue</div>
              </div>
              <div style={statBox('#2563eb')}>
                <div style={{ fontSize:22, fontWeight:700, color:'#2563eb' }}>${summary.revenue.collected.toFixed(2)}</div>
                <div style={{ fontSize:12, color:'#6b7280' }}>Collected</div>
              </div>
              <div style={statBox('#7c3aed')}>
                <div style={{ fontSize:22, fontWeight:700, color:'#7c3aed' }}>${summary.revenue.subtotal.toFixed(2)}</div>
                <div style={{ fontSize:12, color:'#6b7280' }}>Subtotal (excl. delivery)</div>
              </div>
              <div style={statBox('#f59e0b')}>
                <div style={{ fontSize:22, fontWeight:700, color:'#f59e0b' }}>${summary.revenue.delivery_fees.toFixed(2)}</div>
                <div style={{ fontSize:12, color:'#6b7280' }}>Delivery Fees</div>
              </div>
            </div>
          </div>

          {/* Bills + Orders */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
            <div style={card}>
              <div style={{ fontSize:14, fontWeight:700, color:'#111827', marginBottom:14 }}>🧾 Bills</div>
              {[['Total',summary.bills.total,'#374151'],['Delivered',summary.bills.delivered,'#16a34a'],['Cancelled',summary.bills.cancelled,'#dc2626'],['Paid',summary.bills.paid,'#2563eb'],['Unpaid',summary.bills.unpaid,'#f59e0b']].map(([label,val,color])=>(
                <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #f3f4f6', fontSize:13 }}>
                  <span style={{ color:'#6b7280' }}>{label}</span>
                  <span style={{ fontWeight:700, color }}>{val}</span>
                </div>
              ))}
            </div>
            <div style={card}>
              <div style={{ fontSize:14, fontWeight:700, color:'#111827', marginBottom:14 }}>📦 Orders & Customers</div>
              {[['Total Orders',summary.orders.total,'#374151'],['Orders GMV',`$${summary.orders.gmv.toFixed(2)}`,'#16a34a'],['New Customers',summary.new_customers,'#7c3aed']].map(([label,val,color])=>(
                <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #f3f4f6', fontSize:13 }}>
                  <span style={{ color:'#6b7280' }}>{label}</span>
                  <span style={{ fontWeight:700, color }}>{val}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Staff performance */}
      {staff.length>0&&(
        <div style={card}>
          <div style={{ fontSize:14, fontWeight:700, color:'#111827', marginBottom:14 }}>👥 Staff Performance</div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ background:'#f9fafb', borderBottom:'1px solid #e5e7eb' }}>
                {['Staff','Orders Created','Orders Value','Bills Created','Bills Value'].map(h=>(
                  <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:600, color:'#374151', fontSize:12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {staff.map(s=>(
                <tr key={s.user_id} style={{ borderBottom:'1px solid #f3f4f6' }}>
                  <td style={{ padding:'8px 12px', fontWeight:600, color:'#111827' }}>{s.username}</td>
                  <td style={{ padding:'8px 12px', textAlign:'center' }}>{s.orders_created}</td>
                  <td style={{ padding:'8px 12px', color:'#16a34a', fontWeight:600 }}>${s.orders_value.toFixed(2)}</td>
                  <td style={{ padding:'8px 12px', textAlign:'center' }}>{s.bills_created}</td>
                  <td style={{ padding:'8px 12px', color:'#7c6af5', fontWeight:600 }}>${s.bills_value.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delivery stats */}
      {delivery.length>0&&(
        <div style={card}>
          <div style={{ fontSize:14, fontWeight:700, color:'#111827', marginBottom:14 }}>🚚 Delivery Sessions</div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ background:'#f9fafb', borderBottom:'1px solid #e5e7eb' }}>
                {['Type','Status','Sessions','Packages','Sent','Returned','Cash USD','Payout KHR'].map(h=>(
                  <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:600, color:'#374151', fontSize:12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {delivery.map((d,i)=>(
                <tr key={i} style={{ borderBottom:'1px solid #f3f4f6' }}>
                  <td style={{ padding:'8px 12px', fontWeight:600 }}>{d.delivery_type}</td>
                  <td style={{ padding:'8px 12px' }}><span style={{ fontSize:11, background:d.status==='closed'?'#f3f4f6':'#dcfce7', color:d.status==='closed'?'#6b7280':'#16a34a', padding:'2px 8px', borderRadius:8 }}>{d.status}</span></td>
                  <td style={{ padding:'8px 12px', textAlign:'center' }}>{d.sessions}</td>
                  <td style={{ padding:'8px 12px', textAlign:'center' }}>{d.packages}</td>
                  <td style={{ padding:'8px 12px', textAlign:'center', color:'#16a34a', fontWeight:600 }}>{d.sent}</td>
                  <td style={{ padding:'8px 12px', textAlign:'center', color:'#dc2626' }}>{d.returned}</td>
                  <td style={{ padding:'8px 12px', color:'#16a34a', fontWeight:600 }}>${d.cash_usd.toFixed(2)}</td>
                  <td style={{ padding:'8px 12px', color:'#f59e0b' }}>{d.payout_khr.toLocaleString()}៛</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
